import { useEffect, useMemo, useState } from "react";
import Api from "../../../shared/api/api";
import ImagePreview from "./ImagePreview";

const LEVELS = [
  "Junior",
  "Intermediate",
  "Senior",
];

const createQuestionFormData = (questions) =>
  questions.reduce((formData, question) => {
    const key = `${question.test_id}-${question.level}-${question.id}`;

    formData[key] = {
      question: question.question || "",
      choice_a: question.choice_a || "",
      choice_b: question.choice_b || "",
      choice_c: question.choice_c || "",
      choice_d: question.choice_d || "",
      answer: question.answer || "",
      level: question.level || "",
      image: question.image || "",
    };

    return formData;
  }, {});

export default function QuestionsAdminList({
  questions = [],
  tests = [],
  setQuestions,
}) {
  const [formData, setFormData] = useState({});
  const [visibleLevels, setVisibleLevels] = useState({});
  const [updatingKey, setUpdatingKey] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setFormData(createQuestionFormData(questions));
  }, [questions]);

  const testsWithQuestions = useMemo(
    () =>
      tests.filter((test) =>
        questions.some(
          (question) =>
            String(question.test_id) ===
            String(test.id)
        )
      ),
    [questions, tests]
  );

  const getQuestionKey = (question) =>
    `${question.test_id}-${question.level}-${question.id}`;

  const handleInputChange = (
    question,
    field,
    value
  ) => {
    const questionKey = getQuestionKey(question);

    setFormData((currentData) => ({
      ...currentData,
      [questionKey]: {
        ...currentData[questionKey],
        [field]: value,
      },
    }));

    setError("");
  };

  const handleImageUpload = (
    question,
    event
  ) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setError("Please select an image file.");
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const result = String(reader.result || "");
      const base64String = result.includes(",")
        ? result.split(",")[1]
        : result;

      handleInputChange(
        question,
        "image",
        base64String
      );
    };

    reader.onerror = () => {
      setError("The image could not be read.");
    };

    reader.readAsDataURL(file);
  };

  const updateQuestion = async (question) => {
    const questionKey = getQuestionKey(question);
    const updates = formData[questionKey];

    if (!updates) {
      return;
    }

    setUpdatingKey(questionKey);
    setError("");

    try {
      const response = await Api.updateQuestion(
        question.test_id,
        question.id,
        question.level,
        updates
      );

      if (response?.error) {
        throw new Error(response.error);
      }

      if (typeof setQuestions === "function") {
        setQuestions((currentQuestions) =>
          currentQuestions.map((currentQuestion) =>
            getQuestionKey(currentQuestion) ===
            questionKey
              ? {
                  ...currentQuestion,
                  ...updates,
                }
              : currentQuestion
          )
        );
      }
    } catch (requestError) {
      console.error(
        "Unable to update FourH question:",
        requestError
      );

      setError(
        requestError?.message ||
          "The question could not be updated."
      );
    } finally {
      setUpdatingKey("");
    }
  };

  const toggleLevelVisibility = (
    level,
    testId
  ) => {
    const visibilityKey = `${level}-${testId}`;

    setVisibleLevels((currentLevels) => ({
      ...currentLevels,
      [visibilityKey]:
        !currentLevels[visibilityKey],
    }));
  };

  const renderQuestions = (
    level,
    testId
  ) => {
    const levelQuestions = questions.filter(
      (question) =>
        question.level === level &&
        String(question.test_id) ===
          String(testId)
    );

    if (levelQuestions.length === 0) {
      return (
        <p className="fourHEmptyMessage">
          No {level.toLowerCase()} questions were found.
        </p>
      );
    }

    return (
      <div className="fourHTableWrapper">
        <table className="fourHAdminTable">
          <thead>
            <tr>
              <th scope="col">Question ID</th>
              <th scope="col">Question</th>
              <th scope="col">Choice A</th>
              <th scope="col">Choice B</th>
              <th scope="col">Choice C</th>
              <th scope="col">Choice D</th>
              <th scope="col">Answer</th>
              <th scope="col">Type</th>
              <th scope="col">Level</th>
              <th scope="col">Image</th>
              <th scope="col">Action</th>
            </tr>
          </thead>

          <tbody>
            {levelQuestions.map((question) => {
              const questionKey =
                getQuestionKey(question);

              const currentData =
                formData[questionKey] || {};

              const isKnowledge =
                question.question_type ===
                "Knowledge";

              const isUpdating =
                updatingKey === questionKey;

              return (
                <tr key={questionKey}>
                  <td data-label="Question ID">
                    <strong>{question.id}</strong>
                  </td>

                  <td data-label="Question">
                    <textarea
                      className="questionUpdateTextArea"
                      value={
                        currentData.question || ""
                      }
                      onChange={(event) =>
                        handleInputChange(
                          question,
                          "question",
                          event.target.value
                        )
                      }
                    />
                  </td>

                  {[
                    "choice_a",
                    "choice_b",
                    "choice_c",
                    "choice_d",
                  ].map((field) => (
                    <td
                      key={field}
                      data-label={field.replace(
                        "_",
                        " "
                      )}
                    >
                      {isKnowledge ? (
                        <textarea
                          className="questionUpdateInputs"
                          value={
                            currentData[field] || ""
                          }
                          onChange={(event) =>
                            handleInputChange(
                              question,
                              field,
                              event.target.value
                            )
                          }
                        />
                      ) : (
                        <span className="notApplicable">
                          Not Applicable
                        </span>
                      )}
                    </td>
                  ))}

                  <td data-label="Answer">
                    <textarea
                      className="questionUpdateInputs"
                      value={
                        currentData.answer || ""
                      }
                      onChange={(event) =>
                        handleInputChange(
                          question,
                          "answer",
                          event.target.value
                        )
                      }
                    />
                  </td>

                  <td data-label="Question Type">
                    {question.question_type}
                  </td>

                  <td data-label="Level">
                    <select
                      className="questionUpdateInputs"
                      value={
                        currentData.level || level
                      }
                      onChange={(event) =>
                        handleInputChange(
                          question,
                          "level",
                          event.target.value
                        )
                      }
                    >
                      {LEVELS.map((levelOption) => (
                        <option
                          key={levelOption}
                          value={levelOption}
                        >
                          {levelOption}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td data-label="Image">
                    {currentData.image && (
                      <ImagePreview
                        base64String={
                          currentData.image
                        }
                        alt={`Question ${question.id}`}
                        className="adminQuestionImage"
                      />
                    )}

                    <input
                      type="file"
                      className="questionUpdateInputs"
                      accept="image/*"
                      onChange={(event) =>
                        handleImageUpload(
                          question,
                          event
                        )
                      }
                    />
                  </td>

                  <td data-label="Action">
                    <button
                      type="button"
                      className="updateUserBtn"
                      onClick={() =>
                        updateQuestion(question)
                      }
                      disabled={isUpdating}
                    >
                      {isUpdating
                        ? "Updating..."
                        : "Update Question"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <section className="questionsListDiv">
      <h1 className="adminHeaders">
        Questions Per Test
      </h1>

      {error && (
        <p className="fourHFormStatus error" role="alert">
          {error}
        </p>
      )}

      {testsWithQuestions.length === 0 ? (
        <p className="fourHEmptyMessage">
          No questions were found.
        </p>
      ) : (
        testsWithQuestions.map((test) => (
          <section
            className="listOfQuestions"
            key={test.id}
          >
            <h2 className="adminSubHeaders">
              Test ID: {test.id}
            </h2>

            {LEVELS.map((level) => {
              const visibilityKey = `${level}-${test.id}`;
              const isVisible =
                Boolean(
                  visibleLevels[visibilityKey]
                );

              return (
                <div
                  className="questionLevelSection"
                  key={visibilityKey}
                >
                  <button
                    type="button"
                    className="adminSubHeaders adminSectionToggle"
                    onClick={() =>
                      toggleLevelVisibility(
                        level,
                        test.id
                      )
                    }
                    aria-expanded={isVisible}
                  >
                    <span>{level} Level</span>
                    <span aria-hidden="true">
                      {isVisible ? "−" : "+"}
                    </span>
                  </button>

                  {isVisible &&
                    renderQuestions(
                      level,
                      test.id
                    )}
                </div>
              );
            })}
          </section>
        ))
      )}
    </section>
  );
}